import request from '@/modules/base/util/request'
import config from 'config'
import { pickBy } from 'lodash'
import { DeliveryConditionService } from '@/modules/settings/components/delivery-conditions/deliveryConditionService'
import { PayConditionService } from '@/modules/settings/components/pay-conditions/payConditionService'
import getCollaborationRequestHeader from '../document/_helpers/getCollaborationRequestHeader'
import { parseNextBusinessPartnerNumberResponse } from '@/modules/base/util/parseNextBusinessPartnerNumberResponse'

const baseUrl = `${config.resourceHost}`

export const buildAndCreateCustomer = (data, name) => {
  return new Promise((resolve, reject) => {
    const newCustomerData = {
      name,
      category: '',
      discount: 0,
      addresses: [],
      notesAlert: false,
      customerDefaultAddress: {
        billingAddress: {
          ...data,
          isDefault: true,
        },
        billingIsDeliveryAddress: true,
      },
    }
    Promise.all([
      DeliveryConditionService.getAll(),
      PayConditionService.getAll(),
      CustomerService.getNextNumber(),
    ]).then(([deliveryConditions, payConditions, nextNumbersResponse]) => {
      const pair = parseNextBusinessPartnerNumberResponse(nextNumbersResponse)
      const defaultDelCondition = deliveryConditions.find(delCondition => delCondition.isDefault)
      const defaultPayCondition = payConditions.find(payCondition => payCondition.isDefault)
      newCustomerData.deliveryConditionId = defaultDelCondition.id
      newCustomerData.payConditionId = defaultPayCondition.id
      const payload = { number: pair.number, ...newCustomerData }
      payload.accountNo = pair.accountNo
      CustomerService.create(payload)
        .then(resolve)
        .catch(reject)
    }).catch(reject)
  })
}

export const CustomerService = {
  create: (customer) => {
    return new Promise((resolve, reject) => {
      request(`${baseUrl}customer`, {
        method: 'POST',
        auth: true,
        isConcurrent: true,
        data: customer,
      })
        .then(response => {
          const { body: { data } } = response
          resolve(data)
        })
        .catch(error => reject(error))
    })
  },

  edit: (customer) => {
    return new Promise((resolve, reject) => {
      const { id } = customer
      request(`${baseUrl}customer/${id}`, {
        method: 'PUT',
        auth: true,
        isConcurrent: true,
        data: customer,
      })
        .then(response => {
          const { body: { data } } = response
          resolve(data)
        })
        .catch(error => reject(error))
    })
  },

  updateTags: (customerId, tags) => {
    return new Promise((resolve, reject) => {
      request(`${baseUrl}customer/${customerId}/tags`, {
        method: 'PUT',
        auth: true,
        isConcurrent: true,
        data: { tags },
      })
        .then(response => {
          const { body: { data } } = response
          resolve(data)
        })
        .catch(error => reject(error))
    })
  },

  getById: (id) => {
    return new Promise((resolve, reject) => {
      request(`${baseUrl}customer/${id}`, {
        auth: true,
        isConcurrent: true,
      })
        .then(response => {
          const { body: { data } } = response
          resolve(data)
        })
        .catch(error => reject(error))
    })
  },

  find: (searchTerm, offset = null, limit = null, hasEmail = undefined) => {
    const query = new URLSearchParams(pickBy({ limit, offset, hasEmail }, Boolean))
    return new Promise((resolve, reject) => {
      request(`${baseUrl}find/customer/${encodeURIComponent(searchTerm) || '*'}?${query.toString()}`, {
        method: 'GET',
        auth: true,
        isConcurrent: true,
      })
        .then(response => {
          const { body } = response
          resolve(body)
        })
        .catch(error => reject(error))
    })
  },

  getAll: (options) => {
    const { offset, limit, searchTerm } = options

    return new Promise((resolve, reject) => {
      const finalOffset = searchTerm ? 0 : offset
      const url = `${baseUrl}customer?offset=${finalOffset}&limit=${limit}&search=${encodeURIComponent(searchTerm || '')}`
      return request(url, { auth: true, isConcurrent: true })
        .then(response => {
          const { body } = response
          resolve(body)
        })
        .catch(error => reject(error))
    })
  },

  getAllSSR: (options) => {
    const { offset = 0, limit, searchTerm, orderBy } = options
    const dataToSend = {
      startRow: offset,
      endRow: limit ? offset + limit : null,
      searchTerm,
      sortModel: orderBy || [],

    }

    return new Promise((resolve, reject) => {
      const url = `${baseUrl}customers`
      return request(url, {
        method: 'POST',
        auth: true,
        isConcurrent: true,
        timeout: config.requestExtraLongTimeout,
        data: dataToSend,
      }).then(response => {
        const { body } = response
        resolve(body)
      })
        .catch(error => reject(error))
    })
  },

  getNextNumber: () => {
    return new Promise((resolve, reject) => {
      const url = `${baseUrl}customer/number`
      return request(url, { auth: true, isConcurrent: true })
        .then(response => {
          const { body } = response
          resolve(body)
        })
        .catch(error => reject(error))
    })
  },

  delete: (id) => {
    return new Promise((resolve, reject) => {
      return request(`${baseUrl}customer/${id}`, {
        auth: true,
        isConcurrent: true,
        method: 'DELETE',
      })
        .then((result) => resolve(result))
        .catch(error => reject(error))
    })
  },

  validateVatNumber: ({ vatNumber, companyName = '', city = '', zipCode = '', street = '' }, collaborationId = undefined) => {
    return new Promise((resolve, reject) => {
      return request(`${baseUrl}customer/vat/${vatNumber}`, {
        auth: true,
        isConcurrent: true,
        data: {
          city, companyName, street, zipCode,
        },
        headers: getCollaborationRequestHeader(collaborationId),
      })
        .then((result) => resolve(result))
        .catch(error => reject(error))
    })
  },

  getCustomersWithBuyerReferenceId: async(buyerReferenceId) => {
    const response = await request(`${baseUrl}customer/buyerreference/${encodeURIComponent(buyerReferenceId)}`, {
      auth: true,
    })
    return response.body.data
  },
}

export default CustomerService
